// 게시 에셋 매니페스트(슬롯키 → 이미지 URL). 관리자가 게시한 실물 이미지를 앱이 읽는다.
// 없으면 빈 매니페스트 → 각 로더가 더미 폴백(에셋 지연이 서비스를 막지 않음).
import { EDGE_BASE } from "./apiBase";
import { artUrl } from "./artVersion";
import { fetchEarlyOr } from "./earlyFetch";
/**
 * 🔴 **정적 매니페스트는 받아오지 않고 번들에 넣는다.**
 *
 * 예전엔 `/content/asset-manifest.json` 을 받아 왔다. 그 사이(첫 화면 기준 실측 0.7~2.5초)
 * 아이가 보는 것은 **번들 기본 그림도 아닌 절차적 폴백**이었다 — 파란 그라데이션 무대에
 * 몸통만 있는 아바타. 진짜 그림은 그 뒤에 슬쩍 바뀌어 나타난다.
 *
 * 이 파일은 **빌드 산출물**이라(`content:build`) 어차피 배포와 함께 굳는다. 받아올 이유가
 * 없다 — 번들에 넣으면 첫 그림부터 제대로 나오고 요청도 하나 준다(39KB).
 * 운영자가 올린 것(게시본)은 여전히 엣지에서 늦게 와서 그 위에 덮는다.
 */
import STATIC_MANIFEST from "../../public/content/asset-manifest.json";
import {
  mergeAssetManifests,
  resolveEdgeAsset,
  type AssetManifest,
  type AssetManifestEntry,
} from "@greg-chick/shared";

export type { AssetManifest, AssetManifestEntry };

let cache: AssetManifest | null = null;

// lanhttps(휴대폰 HTTPS 테스트) 모드에서만 설정됨 — 매니페스트의 절대 엣지 URL을 상대경로로
// 바꿔 동일출처 프록시로 로드한다(HTTPS 혼합콘텐츠·폰 localhost 문제 회피). 일반 dev/운영은 빈 값=무동작.
const PROXY_EDGE = (import.meta.env.VITE_PROXY_EDGE ?? "").replace(/\/$/, "");
function rewriteEdgeUrl(url?: string): string | undefined {
  if (!url || !PROXY_EDGE) return url;
  return url.startsWith(`${PROXY_EDGE}/`) ? url.slice(PROXY_EDGE.length) : url;
}
/**
 * 게시본은 에셋을 **상대 경로**(`/assets/…`)로 저장한다 — 운영자 PC 주소가 박히지 않게
 * (`shared/assetUrl.ts`). 운영은 웹=엣지 동일 출처라 그대로 쓰이고, **dev 에서만** 여기서
 * 엣지 주소를 붙인다. 안 붙이면 :5173 이 자기 자신에게 물어 404 → 그림이 통째로 안 나온다.
 */
function rewriteManifestAssets(m: AssetManifest): AssetManifest {
  const assets: Record<string, AssetManifestEntry> = {};
  for (const [k, e] of Object.entries(m.assets)) {
    // 🔴 `/art/` 는 이름이 고정이라 고친 그림이 네 시간 안 내려간다 — 내용 도장을 붙인다
    const url = artUrl(rewriteEdgeUrl(resolveEdgeAsset(e.url, EDGE_BASE))!);
    const prev = artUrl(e.prev ? rewriteEdgeUrl(resolveEdgeAsset(e.prev, EDGE_BASE)) : undefined);
    assets[k] = { ...e, url, ...(prev ? { prev } : {}) };
  }
  return { ...m, assets };
}

/** 검사에서 이 배선(도장 붙이기·dev 주소 붙이기)을 그대로 걸어 보게. */
export function rewriteForTest(m: AssetManifest): AssetManifest {
  return rewriteManifestAssets(m);
}

async function getManifest(url: string, init: RequestInit): Promise<AssetManifest | null> {
  try {
    // index.html 이 먼저 띄운 것이 있으면 그걸 쓴다(책 화면 · lib/earlyFetch)
    const r = await fetchEarlyOr(url, init);
    if (r.ok) {
      const m = (await r.json()) as AssetManifest;
      if (m && typeof m.assets === "object") return m;
    }
  } catch {
    /* 무시 */
  }
  return null;
}

/**
 * 게시본(엣지)을 기다려 줄 시간. 넘기면 정적 아트만으로 먼저 그린다.
 *
 * 예전엔 `Promise.all` 이라 **엣지가 느리면 첫 화면이 통째로 늦었다** — 엣지가 죽어 있으면
 * fetch 가 실패로 끝날 때까지 아무것도 안 나왔다. "서버 없이도 M1 은 완전히 동작한다"
 * (불변식 3)에 어긋나고, 4G LCP 예산(2.5s)도 여기서 다 쓴다.
 * 정적 매니페스트는 동일 출처 + 캐시라 거의 즉시 온다.
 */
const EDGE_BUDGET_MS = 1200;

/** 시간 안에 안 오면 null — 실패가 아니라 "이번엔 없는 셈 치자"다. */
function withDeadline<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), ms);
    void p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(null);
      },
    );
  });
}

// 정적 기본 아트(항상 존재) **아래에** 게시본을 덮어쓴다 → 기본 아트(바닥·장식·타일·배경)는
// 게시본에서 누락돼도 항상 보장되고, 운영자가 올린/덮어쓴 키는 게시본이 우선한다.
/**
 * 🔴 **같은 때에 여러 곳이 부른다.**
 *
 * 첫 화면 하나에서 히어로 아바타·무대 캐릭터·배경이 각자 이 함수를 부른다. 캐시는 **끝난 뒤에만**
 * 채워지므로, 동시에 부르면 전부 캐시가 비어 있는 것을 보고 **각자 요청을 날린다**.
 * 실측(2026-08-22): 첫 화면에서 매니페스트를 **7번씩** 받아 왔다(엣지 7 + 정적 7 = 14요청).
 * 화면은 멀쩡히 떠서 아무도 몰랐다 — 폰에서는 그만큼 첫 그림이 늦는다.
 *
 * 그래서 **진행 중인 약속을 나눠 쓴다**(대사 풀·문답이 이미 쓰는 방식).
 */
let inflight: Promise<AssetManifest> | null = null;

export async function loadAssetManifest(): Promise<AssetManifest> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = loadFresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

const STATIC = STATIC_MANIFEST as unknown as AssetManifest;

/**
 * 🖼️ **지금 당장 그릴 수 있는 매니페스트** — 기다리지 않는다.
 *
 * 게시본이 아직 안 왔으면 번들에 든 정적 아트로 그린다. 첫 화면이 **처음부터 제대로**
 * 보이는 것이 이 함수의 전부다 — 운영자가 덮어쓴 그림은 몇 백 ms 뒤 `loadAssetManifest`
 * 가 끝나면 그때 바뀐다(대부분은 같은 그림이라 눈에 안 띈다).
 */
let staticReady: AssetManifest | null = null;
export function assetManifestNow(): AssetManifest {
  if (cache) return cache;
  staticReady ??= rewriteManifestAssets({
    version: STATIC.version ?? 0,
    assets: STATIC.assets ?? {},
  });
  return staticReady;
}

async function loadFresh(): Promise<AssetManifest> {
  const edgeP = getManifest(`${EDGE_BASE}/api/asset-manifest`, { cache: "no-store" });
  const edge = await withDeadline(edgeP, EDGE_BUDGET_MS);
  const stat = STATIC;
  // 합치기는 shared 한 곳(mergeAssetManifests) — 관리자도 같은 함수를 쓴다.
  // 게시본이 「지움」으로 표시한 자리는 여기서 떨어진다(안 그러면 정적 그림이 되살아난다).
  cache = rewriteManifestAssets({
    version: edge?.version ?? stat?.version ?? 0,
    assets: mergeAssetManifests(stat?.assets ?? {}, edge?.assets ?? {}),
  });

  // 늦게 온 게시본은 버리지 않는다 — 캐시를 비워 **다음 로드**가 집어 가게 한다.
  // 지금 그려진 화면을 뒤늦게 갈아엎지는 않는다(아이 눈앞에서 그림이 바뀌면 더 이상하다).
  if (!edge) {
    void edgeP.then((late) => {
      if (late && cache) cache = null;
    });
  }
  return cache;
}

/** 캐시 무효화(실시간 게시 수신 시 다음 로드가 게시본을 다시 읽도록). */
export function resetAssetManifestCache(): void {
  cache = null;
}

export function manifestEntry(m: AssetManifest, key: string): AssetManifestEntry | undefined {
  return m.assets?.[key];
}
